import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import type { GeoJSONSource } from "mapbox-gl";
import { loadSvgIconToMapbox } from "../utils/common";
import { CountIcon, FlightIcon, LocationIcon, WarningIcon } from "../icons";
import 'mapbox-gl/dist/mapbox-gl.css';
import '../style/map.less';
import { ExpandIcon, FilterIcon, MinusIcon, PlusIcon, RefreshIcon } from "../icons";
import FilterComponent, { FilterComponentRef, FilterObject } from "./FilterComponent";
// import FlightComponent from "./FlightComponent";
import FlightPopup from "./FlightPopup";
// import { getMapData } from "../../service/mapDataService";
import { fetchActiveFlights, fetchActiveAirports } from "../../service/travalerMapService";

import { getMapCenterCoordinates, getMapZoomLevel, getUserCountryInfo, fetchUserLocation } from "../../service/locationService";
import { getProperty } from "../../fsConfig";
import { clearTravelerMapCache } from "../utils/cacheUtils";

// -- Compute position for arc --
function computeCurrentPositionArc(flight: any): [number, number] {
  if (!flight || !flight.start || !flight.end) return flight?.start ?? [0, 0];

  const { depMs, arrMs, start, end } = flight;
  const now = Date.now();

  if (depMs != null && arrMs != null && arrMs > depMs) {
    const t = Math.min(Math.max((now - depMs) / (arrMs - depMs), 0), 1);
    return interpolateGreatCircle(start, end, t);
  }

  return start;
}

function generateGreatCircleCoords(start: [number, number], end: [number, number], segments = 64) {
  const coords: [number, number][] = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    coords.push(interpolateGreatCircle(start, end, t));
  }
  return coords;
}

function interpolateGreatCircle(start: [number, number], end: [number, number], t: number): [number, number] {
  const toRad = (d: number) => d * Math.PI / 180;
  const toDeg = (r: number) => r * 180 / Math.PI;
  const [lon1, lat1] = start.map(toRad);
  const [lon2, lat2] = end.map(toRad);

  const d = 2 * Math.asin(
    Math.sqrt(
      Math.sin((lat2 - lat1) / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) *
      Math.sin((lon2 - lon1) / 2) ** 2
    )
  );

  if (d === 0) return start;

  const A = Math.sin((1 - t) * d) / Math.sin(d);
  const B = Math.sin(t * d) / Math.sin(d);

  const x = A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2);
  const y = A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2);
  const z = A * Math.sin(lat1) + B * Math.sin(lat2);

  const lat = Math.atan2(z, Math.sqrt(x * x + y * y));
  const lon = Math.atan2(y, x);
  return [toDeg(lon), toDeg(lat)];
}




const MapComponent = () => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [modeChange, setModeChange] = useState<string>('dark');
  const [accessToken, setAccessToken] = useState<string>('');
  const [showDetailedFilter, setShowDetailedFilter] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState<string>('00:00');
  const [mapIns, setMapIns] = useState<mapboxgl.Map>();
  const [refreshStep, setRefreshStep] = useState<number>(1);
  
  const workerRef = useRef<Worker | null>(null);
  const [mapReady, setMapReady] = useState(false);
  // keep flight compact data accessible to main thread
  const flightMirrorCache = useRef<Map<number, any>>(new Map());
  const airportMirrorCache = useRef<Map<number, any>>(new Map());
  const [hoveredFlight, setHoveredFlight] = useState<{
    id: number;
    lngLat: mapboxgl.LngLatLike;
    info: any;
    popupShouldBeOnLeft?: boolean;
  } | null>(null);

  // Function to calculate optimal popup positioning
  const calculatePopupPositioning = (lngLat: mapboxgl.LngLatLike): boolean => {
    if (!mapRef.current) return false;
    
    const map = mapRef.current;
    const point = map.project(lngLat);
    const mapContainerRect = map.getContainer().getBoundingClientRect();
    const pointScreenX = point.x + mapContainerRect.left;
    
    // Constants for popup dimensions (matching FlightPopup.tsx)
    const HOVER_POPUP_WIDTH = 220;
    const DETAIL_POPUP_WIDTH = 405;
    const POPUP_OFFSET = 20;
    const POPUP_SPACING = 5;
    
    // Calculate total width needed for both popups
    const totalWidth = HOVER_POPUP_WIDTH + POPUP_SPACING + DETAIL_POPUP_WIDTH;
    
    // Check available space on both sides
    const availableRightSpace = mapContainerRect.right - pointScreenX;
    const availableLeftSpace = pointScreenX - mapContainerRect.left;
    
    // Determine positioning: Default to RIGHT side, only use LEFT when insufficient space
    if (availableRightSpace >= (totalWidth + POPUP_OFFSET)) {
      return false; // Use right side
    } else if (availableLeftSpace >= (totalWidth + POPUP_OFFSET)) {
      return true; // Use left side
    } else {
      // Neither side has sufficient space, choose the side with more available space
      return availableLeftSpace > availableRightSpace;
    }
  };

  const sessionData = getProperty('session', {});  
  const currentUsername = sessionData?.name || "";
  const currentUserId = sessionData?.id || sessionData?.userId || "";
  

  // Filter suggestions derived from flight data
  const [filterSuggestions, setFilterSuggestions] = useState<{
    passengerOptions: Array<{tag: string, label: string, value: string, originalValue?: string}>;
    tripGroupOptions: Array<{tag: string, label: string, value: string, originalValue?: string}>;
    assigneeOptions: Array<{tag: string, label: string, value: string, originalValue?: string}>;
    recordLocatorOptions: Array<{tag: string, label: string, value: string, originalValue?: string}>;
    agentNameOptions: Array<{tag: string, label: string, value: string, originalValue?: string}>;
  }>({
    passengerOptions: [],
    tripGroupOptions: [],
    assigneeOptions: [],
    recordLocatorOptions: [],
    agentNameOptions: []
  });  

  // Initialize filter state from sessionStorage or use defaults
  const getInitialFilterState = (): FilterObject => {
    try {
      // If no current user, clear any cached data to prevent data leakage
      if (!currentUsername) {
        clearTravelerMapCache();
        return {
          selectionType: 'All',
          referenceNumber: [],
          agentName: [],
          tripGroup: [],
          passengerName: []
        };
      }

      const savedFilters = sessionStorage.getItem('mapFilters');
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters);
       
        if (typeof parsedFilters.referenceNumber === 'string' && parsedFilters.referenceNumber) {
          parsedFilters.referenceNumber = [{
            tag: parsedFilters.referenceNumber,
            label: parsedFilters.referenceNumber,
            value: parsedFilters.referenceNumber
          }];
        } else if (typeof parsedFilters.referenceNumber === 'string') {
          parsedFilters.referenceNumber = [];
        }
       
        if (typeof parsedFilters.agentName === 'string' && parsedFilters.agentName) {
          parsedFilters.agentName = [{
            tag: parsedFilters.agentName,
            label: parsedFilters.agentName,
            value: parsedFilters.agentName
          }];
        } else if (typeof parsedFilters.agentName === 'string') {
          parsedFilters.agentName = [];
        }
       
        return parsedFilters;
      }
    } catch (error) {
      console.error('Error loading saved filters:', error);
      // Clear corrupted cache
      clearTravelerMapCache();
    }
    return {
      selectionType: 'All',
      referenceNumber: [],
      agentName: [],
      tripGroup: [],
      passengerName: []
    };
  };

  const [selectionFilter, setSelectionFilter] = useState<FilterObject>(getInitialFilterState());

  useEffect(() => {
    if (currentUsername && workerRef.current && mapReady) {
      applyFiltersUsingWorker(selectionFilter);
    }
  }, [currentUsername, mapReady, selectionFilter]);

  // Track user changes and clear cache when user switches
  const [lastSeenUser, setLastSeenUser] = useState<string>(currentUsername);
  
  useEffect(() => {
    if (lastSeenUser && currentUsername && lastSeenUser !== currentUsername) {
      // User has changed, clear the map cache to prevent data leakage
      clearTravelerMapCache();
      
      // Reset filters to default for the new user
      const defaultFilters = {
        selectionType: 'All',
        referenceNumber: [],
        agentName: [],
        tripGroup: [],
        passengerName: []
      };
      setSelectionFilter(defaultFilters);
      setSelectionFilterCount(1);
    }
    setLastSeenUser(currentUsername);
  }, [currentUsername, lastSeenUser]);

  const calculateFilterCount = (filterObj: FilterObject): number => {
    let count = 1; // Base count is always 1 (represents the radio button selection: All/Issues/My Issues)
 
    // Add count for multi-select filters (these are additional filters on top of the base)
    if (filterObj.tripGroup && Array.isArray(filterObj.tripGroup) && filterObj.tripGroup.length > 0) {
      count += filterObj.tripGroup.length;
    }
 
    if (filterObj.passengerName && Array.isArray(filterObj.passengerName) && filterObj.passengerName.length > 0) {
      count += filterObj.passengerName.length;
    }
 
    if (filterObj.referenceNumber && Array.isArray(filterObj.referenceNumber) && filterObj.referenceNumber.length > 0) {
      count += filterObj.referenceNumber.length;
    }
 
    if (filterObj.agentName && Array.isArray(filterObj.agentName) && filterObj.agentName.length > 0) {
      count += filterObj.agentName.length;
    }
 
    // Note: selectionType (radio button) does not add to count since it's part of the base filter
    // The base count of 1 represents whichever radio option is selected (All/Issues/My Issues)
 
    return count;
  };


  
  
  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem('mapFilters', JSON.stringify(selectionFilter));
    } catch (error) {
      console.error('Error saving filters:', error);
    }
  }, [selectionFilter]);
  
  // Calculate initial filter count from saved filters
  
  const [selectionFilterCount, setSelectionFilterCount] = useState<number>(() => {
    const initialFilters = getInitialFilterState();
    const count = calculateFilterCount(initialFilters);
    return count > 0 ? count : 1; // Show at least 1 if no filters are active
  });
  const [mapData, setMapData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true); // Start with loading true for initial load
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [userLocation, setUserLocation] = useState<any>(null);
  const [flightsRendered, setFlightsRendered] = useState<boolean>(false);
  const [airportsRendered, setAirportsRendered] = useState<boolean>(false);
  const [dataFetched, setDataFetched] = useState<boolean>(false);
  const filterRef = useRef<FilterComponentRef>(null);

  // Effect to manage loading state - only hide loading when both flights and airports are rendered
  useEffect(() => {
    if (dataFetched && flightsRendered && airportsRendered) {
      setLoading(false);
    }
  }, [dataFetched, flightsRendered, airportsRendered]);

  const applyFiltersUsingWorker = (filters: FilterObject) => {
    if (!workerRef.current) {
      console.error('Worker not initialized');
      return;
    }
   
    let agentNameArray = [...(filters.agentName || [])];
   
    if (filters.selectionType === 'My issues' && currentUsername) {
      const usernameExists = agentNameArray.some((item: any) => 
        item.value === currentUsername || item.originalValue === currentUsername);
     
      if (!usernameExists) {
        agentNameArray.push({
          tag: currentUsername,
          label: currentUsername,
          value: currentUsername,
          originalValue: currentUsername
        });
      }
    }
   
    const filterData = {
      isCurrentFlightIssue: filters.selectionType === 'Trips with issues' || filters.selectionType === 'My issues' ? true : undefined,
      isFutureFlightIssue: filters.selectionType === 'Trips with issues' || filters.selectionType === 'My issues' ? true : undefined,
     
      isMyIssues: filters.selectionType === 'My issues',
     
      referenceNumber: filters.referenceNumber || [],
      agentName: agentNameArray,
     
      groupNames: filters.tripGroup || [],
      passengerFirst: filters.passengerName || [],
    };

   
    workerRef.current.postMessage({
      type: 'setFlightFilters',
      filters: filterData
    });

    // Apply the same filters to airports for consistency
    const airportFilterData = {
      isCurrentFlightIssue: filterData.isCurrentFlightIssue,
      isFutureFlightIssue: filterData.isFutureFlightIssue,
      passengerFirst: filterData.passengerFirst,
      groupNames: filterData.groupNames,
      recordLocators: filterData.referenceNumber, // Add record locator filtering for airports
      // Use originalValue for agent names to match airport data format (case-sensitive usernames)
      assigneeIds: filterData.agentName.map((agent: any) => agent.originalValue || agent.value || agent),
      airportCodes: [], // No airport code filtering from UI currently
    };

    workerRef.current.postMessage({
      type: 'setAirportFilters',
      filters: airportFilterData
    });
   
  };


  const mapModeChange = () => {
    mapboxgl.accessToken = accessToken;

    // Use server-provided location data if available, otherwise fallback to client-side detection
    let mapCenter: [number, number];
    let mapZoom: number;
    
    if (userLocation) {
      mapCenter = userLocation.coordinates;
      mapZoom = userLocation.zoom;
    } else {
      // Fallback to client-side detection
      mapCenter = getMapCenterCoordinates();
      mapZoom = getMapZoomLevel();
      const userCountryInfo = getUserCountryInfo();
    }

    const map = new mapboxgl.Map({
      container: mapContainerRef.current as unknown as HTMLElement,
      style: (modeChange === 'light') ? 'mapbox://styles/mapbox/light-v10' : 'mapbox://styles/mapbox/dark-v10',
      center: mapCenter,
      zoom: mapZoom,
      renderWorldCopies: false,
      pitchWithRotate: false,
      dragRotate: false,
      touchPitch: false,
    });        

    mapRef.current = map;

    map.on('load', () => {
      map.setLayoutProperty("country-label", "text-size", 10);
      map.setLayoutProperty("state-label", "text-size", 6);
      map.setLayoutProperty("settlement-label", "text-size", 7);
      map.setLayoutProperty("settlement-label", "icon-image", "");
      map.setLayoutProperty("settlement-subdivision-label", "text-size", 7);
      if (modeChange === 'light') {
        map.setPaintProperty('water', 'fill-color', '#f0f4fa');
        map.setPaintProperty('land', 'background-color', '#cfd7e3');
        map.setPaintProperty('landcover', 'fill-color', '#cfd7e3');
        map.setPaintProperty('landuse', 'fill-color', '#cfd7e3');
        if (map.getLayer('place-label')) {
          map.setLayoutProperty('place-label', 'text-size', 10);
        }
      } else {
        map.setPaintProperty('water', 'fill-color', '#0e2233');
        map.setPaintProperty('land', 'background-color', '#1c283a');
        map.setPaintProperty('landcover', 'fill-color', '#1c283a');
        map.setPaintProperty('landuse', 'fill-color', '#1c283a');
        if (map.getLayer('place-label')) {
          map.setLayoutProperty('place-label', 'text-size', 10);
        }
      }

      // Register flight icons once
      try {
        loadSvgIconToMapbox(map, "flight-green", FlightIcon("#09AF91"));
        loadSvgIconToMapbox(map, "flight-red", FlightIcon("#FB4B4B"));
        loadSvgIconToMapbox(map, "flight-green-warning", FlightIcon("#09AF91"));
        loadSvgIconToMapbox(map, "warning-icon", WarningIcon("#FFD700"));

        loadSvgIconToMapbox(map, "airport-location", LocationIcon());
        loadSvgIconToMapbox(map, "trip-count", CountIcon("#FFFFFF"));      
      } catch (e) {
        console.error("Failed to load SVG icons for flights", e);
      }

      // Add empty GeoJSON sources once (for initialization)
      if (!map.getSource("flights-source")) {
        map.addSource("flights-source", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });

        map.addLayer({
          id: "flights-layer",
          type: "symbol",
          source: "flights-source",
          layout: {
            "icon-image": ["get", "icon"],
            "icon-size": 1.0,
            "icon-allow-overlap": true,
            "icon-rotation-alignment": "map",
            "icon-rotate": ["get", "rotation"],  // use computed rotation
          },
        });

        // overlay warning icon layer
        map.addLayer({
          id: "flights-warning-layer",
          type: "symbol",
          source: "flights-source",
          layout: {
            "icon-image": "warning-icon",
            "icon-size": 0.6,
            "icon-allow-overlap": true,
            "icon-offset": [10, -10],
          },
          filter: ["==", ["get", "showWarning"], true],
        });

        // overlay count icon layer for flights
        map.addLayer({
          id: "flights-count-layer",
          type: "symbol",
          source: "flights-source",
          layout: {
            "icon-image": ["get", "countIcon"],  // dynamically generated SVG icon
            "icon-size": 0.8,
            "icon-allow-overlap": true,
            "icon-anchor": "bottom",
            "icon-offset": [12, -12],  // moves count badge slightly to top-right
          },
          filter: [">", ["get", "tripCount"], 1], // show only if trip count > 1
        });

        // ✅ Interactivity handlers
        // map.on("mouseenter", "flights-layer", () => {
        //   map.getCanvas().style.cursor = "pointer";
        // });

        map.on("mouseleave", "flights-layer", () => {
          map.getCanvas().style.cursor = "";
          const empty: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };
          (map.getSource("flight-path-covered-source") as GeoJSONSource)?.setData(empty);
          (map.getSource("flight-path-remaining-source") as GeoJSONSource)?.setData(empty);
          (map.getSource("flight-airports-source") as GeoJSONSource)?.setData(empty);
        });
        
        map.on("click", "flights-layer", (e) => {
          const feature = e.features?.[0];
          if (feature?.properties?.flightId) {
            // You can trigger popup logic here
          }
        });
        
        map.on("mouseenter", "flights-layer", (e) => {
          const feature = e.features?.[0];
          if (!feature) return;

          const id = feature.properties?.flightId;
          if (!id) return;

          const f = flightMirrorCache.current.get(id);
          if (!f || !f.start || !f.end) return;

          const currentPos = computeCurrentPositionArc(f);

          // ✅ Construct proper passengers array
          const passengers =
            f.trips
              ?.flatMap((trip: any) =>
                (trip.passengers || []).map((p: any) => {
                  // Helper function to format name to title case (fallback)
                  const toTitleCase = (str: string) => {
                    if (!str) return str;
                    return str.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
                  };

                  // Get raw name
                  let rawName: string;
                  if (typeof p === "string") {
                    rawName = p;
                  } else if (p.firstName && p.lastName) {
                    rawName = `${p.firstName} ${p.lastName}`;
                  } else {
                    rawName = p.firstName || p.lastName || p.name || "Unknown";
                  }

                  // Find original casing from filter suggestions if available
                  const originalName = filterSuggestions.passengerOptions?.find(
                    option => option.value === rawName.toLowerCase()
                  )?.originalValue || toTitleCase(rawName);

                  const originalGroupName = trip.groupName ? (
                    filterSuggestions.tripGroupOptions?.find(
                      option => option.value === trip.groupName.toLowerCase()
                    )?.originalValue || toTitleCase(trip.groupName)
                  ) : "Unknown";

                  return {
                    name: originalName,
                    tripId: trip.tripId,
                    pfhId: trip.pfhId,
                    group: originalGroupName,
                    currentFlightHasIssue: trip.currentFlightHasIssue,
                    futureFlightHasIssue: trip.futureFlightHasIssue,
                  };
                })
              ) || [];

          // Get original group name from first trip using filter suggestions
          const firstTripGroupName = f.trips?.[0]?.groupName;
          const originalGroupName = firstTripGroupName ? (
            filterSuggestions.tripGroupOptions?.find(
              option => option.value === firstTripGroupName.toLowerCase()
            )?.originalValue || firstTripGroupName
          ) : "";

          const popupShouldBeOnLeft = calculatePopupPositioning(currentPos);

          setHoveredFlight({
            id,
            lngLat: currentPos,
            popupShouldBeOnLeft,
            info: {
              type: 'flight',
              flightNumber: f.flightNumber,
              dep: f.depAc,
              arr: f.arrAc,
              isCurrentFlightIssue: f.isCurrentFlightIssue,
              isFutureFlightIssue: f.isFutureFlightIssue,
              passengers: passengers,
              group: originalGroupName,
              tripCount: f.trips?.length,
              // recordLocator removed - will be loaded via API when passenger is selected
            },
          });


          const now = Date.now(); // or performance.now() if you prefer
          const depMs = f.depMs, arrMs = f.arrMs;
          const t = Math.min(Math.max((now - depMs) / (arrMs - depMs), 0), 1);
          const current = [
            f.start[0] + (f.end[0] - f.start[0]) * t,
            f.start[1] + (f.end[1] - f.start[1]) * t,
          ];

          
          const allCoords = generateGreatCircleCoords(f.start, f.end, 64);
          const currentIdx = Math.floor(allCoords.length * t);

          const coveredCoords = allCoords.slice(0, currentIdx + 1);
          const remainingCoords = allCoords.slice(currentIdx);

          const covered: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: { type: "LineString", coordinates: coveredCoords },
                properties: {
                  isCurrentFlightIssue: f.isCurrentFlightIssue || false,
                  isFutureFlightIssue: f.isFutureFlightIssue || false,
                },
              },
            ],
          };

          const remaining: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: { type: "LineString", coordinates: remainingCoords },
                properties: {
                  isCurrentFlightIssue: f.isCurrentFlightIssue || false,
                  isFutureFlightIssue: f.isFutureFlightIssue || false,
                },
              },
            ],
          };

          const airports: GeoJSON.FeatureCollection<GeoJSON.Point> = {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: { type: "Point", coordinates: f.start },
                properties: { 
                  type: "departure",
                  isCurrentFlightIssue: f.isCurrentFlightIssue || false,
                  isFutureFlightIssue: f.isFutureFlightIssue || false,
                },
              },
              {
                type: "Feature",
                geometry: { type: "Point", coordinates: f.end },
                properties: { 
                  type: "arrival",
                  isCurrentFlightIssue: f.isCurrentFlightIssue || false,
                  isFutureFlightIssue: f.isFutureFlightIssue || false,
                },
              },
            ],
          };

          (map.getSource("flight-path-covered-source") as mapboxgl.GeoJSONSource)?.setData(covered);
          (map.getSource("flight-path-remaining-source") as mapboxgl.GeoJSONSource)?.setData(remaining);
          (map.getSource("flight-airports-source") as mapboxgl.GeoJSONSource)?.setData(airports);
        });

        

      }


      // --- extra sources for hover visualization ---
      if (!map.getSource("flight-path-covered-source")) {
        map.addSource("flight-path-covered-source", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
        map.addSource("flight-path-remaining-source", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
        map.addSource("flight-airports-source", { type: "geojson", data: { type: "FeatureCollection", features: [] } });

        // solid covered path - use data-driven coloring based on flight issue status
        map.addLayer({
          id: "flight-path-covered-layer",
          type: "line",
          source: "flight-path-covered-source",
          paint: {
            "line-color": [
              "case",
              ["get", "isCurrentFlightIssue"], "#FB4B4B",  
              "#09AF91" // Green for normal flights
            ],
            "line-width": 2,
          },
        });

        // dotted remaining path - use data-driven coloring
        map.addLayer({
          id: "flight-path-remaining-layer",
          type: "line",
          source: "flight-path-remaining-source",
          paint: {
            "line-color": [
              "case",
              ["get", "isCurrentFlightIssue"], "#FB4B4B",
              "#888" // Gray for normal flights
            ],
            "line-width": 2,
            "line-dasharray": [2, 2],
          },
        });

        // departure & arrival markers - use data-driven coloring
        map.addLayer({
          id: "flight-airports-layer",
          type: "circle",
          source: "flight-airports-source",
          paint: {
            "circle-radius": 5,
            "circle-color": [
              "case",
              ["get", "isCurrentFlightIssue"], "#FB4B4B",
              "#09AF91" // Green for normal flights
            ],
            "circle-stroke-color": "#000",
            "circle-stroke-width": 1,
          },
        });
      }


      if (!map.getSource("airports-source")) {
        map.addSource("airports-source", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });

        // Airport location icon layer
        
        map.addLayer({
          id: "airports-layer",
          type: "symbol",
          source: "airports-source",
          layout: {
            "icon-image": "airport-location",
            "icon-size": 1.0,
            "icon-allow-overlap": true,
            "icon-anchor": "bottom",
            "text-field": ["get", "name"],
            "text-size": 10,
            "text-offset": [0, 1.2],
            "text-anchor": "top",
            "text-optional": true,
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          },
          paint: {
            "text-color": (modeChange === "light") ? "#0E2233" : "#FFFFFF",
            "text-halo-color": (modeChange === "light") ? "#FFFFFF" : "#0E2233",
            "text-halo-width": 1.5,
          },
        });

        map.addLayer({
          id: "airports-count-layer",
          type: "symbol",
          source: "airports-source",
          layout: {
            "icon-image": ["get", "countIcon"],  // dynamically generated SVG icon
            "icon-size": 0.8,
            "icon-allow-overlap": true,
            "icon-anchor": "bottom",
            "icon-offset": [12, -12],  // moves count badge slightly to top-right
          },
          filter: [">", ["get", "count"], 0], // show only if count > 0
        });

        map.on("mouseenter", "airports-layer", (e) => {
          const feature = e.features?.[0];
          if (!feature) return;
          

          const id = feature.properties?.id;
          if (!id) return;

          const a = airportMirrorCache.current.get(id);
          if (!a || !a.pos) return;

          // const currentAirportPos = computeCurrentPositionArc(a);
          const currentAirportPos = a.pos;

          // ✅ Construct proper passengers array
          const passengers =
            a.trips
              ?.flatMap((trip: any) =>
                (trip.passengers || []).map((p: any) => {
                  // Helper function to format name to title case (fallback)
                  const toTitleCase = (str: string) => {
                    if (!str) return str;
                    return str.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
                  };
                  
                  // Get passenger name
                  const name = typeof p === "string" ? p : p.name;
                  
                  // Find original casing from filter suggestions if available
                  const originalName = filterSuggestions.passengerOptions?.find(
                    option => option.value === name?.toLowerCase()
                  )?.originalValue || toTitleCase(name || "Unknown");

                  const originalGroupName = trip.groupName ? (
                    filterSuggestions.tripGroupOptions?.find(
                      option => option.value === trip.groupName.toLowerCase()
                    )?.originalValue || toTitleCase(trip.groupName)
                  ) : "Unknown";
                  
                  return {
                    name: originalName,
                    tripId: trip.tripId,
                    pfhId: trip.pfhId,
                    group: originalGroupName,
                    currentFlightHasIssue: trip.currentFlightHasIssue,
                    futureFlightHasIssue: trip.futureFlightHasIssue,
                  };
                })
              ) || [];

          const popupShouldBeOnLeft = calculatePopupPositioning(currentAirportPos);

          setHoveredFlight({
            id,
            lngLat: currentAirportPos,
            popupShouldBeOnLeft,
            info: {
              type: 'airport',
              flightNumber: a.code,
              dep: a.code,
              arr: a.code,
              isCurrentFlightIssue: a.isCurrentFlightIssue,
              isFutureFlightIssue: a.isFutureFlightIssue,
              passengers: passengers,
              group: a.trips?.[0]?.groupName ? (
                filterSuggestions.tripGroupOptions?.find(
                  option => option.value === a.trips[0].groupName.toLowerCase()
                )?.originalValue || a.trips[0].groupName
              ) : "",
              tripCount: a.trips?.length,
              recordLocator: a.trips?.[0]?.recordLocator, // Add record locator from first trip
            },
          });
          
        });

        map.on("mouseleave", "airports-layer", () => {
          map.getCanvas().style.cursor = "";
          // Remove any popup on exit
          const popups = document.getElementsByClassName("mapboxgl-popup");
          if (popups.length) popups[0].remove();
        });
      }





      setMapIns(map);
      setTimeout(() => map.resize(), 300); // ????
    });
  };

  useEffect(() => {
    const fetchTokenAndLocationAndInitMap = async () => {
      try {
        // Fetch both Mapbox token and user location in parallel
        const [tokenResponse, locationData] = await Promise.all([
          fetch('/api/mapboxToken'),
          fetchUserLocation()
        ]);
        
        // Handle Mapbox token
        if (!tokenResponse.ok) {
          let errorMsg = `HTTP error! status: ${tokenResponse.status}`;
          try {
            const errorData = await tokenResponse.json();
            if (errorData && errorData.message) {
              errorMsg += ` - ${errorData.message}`;
            }
          } catch (jsonErr) {
            //
          }
          throw new Error(errorMsg);
        }
        
        let tokenData;
        try {
          tokenData = await tokenResponse.json();
        } catch (jsonErr) {
          throw new Error('Failed to parse Mapbox token response as JSON');
        }
        
        // Set user location data
        setUserLocation(locationData);
        
        // Set access token (this will trigger map initialization)
        setAccessToken(tokenData.token);
      } catch (error) {
        console.error('Failed to load Mapbox token or user location:', error);
        
        // Try to set access token anyway for fallback
        try {
          const response = await fetch('/api/mapboxToken');
          const data = await response.json();
          setAccessToken(data.token);
        } catch (fallbackError) {
          console.error('Fallback token fetch also failed:', fallbackError);
        }
      }
    }
    fetchTokenAndLocationAndInitMap();
  }, []);

  useEffect(() => {
    if (accessToken) {
      mapModeChange();
       
      setMapReady(true); //Trigger the worker and api fetcher - check and delete it
      return () => {
        if (mapRef.current) {
          mapRef.current.remove();
        }
      };
    }
  }, [accessToken, modeChange, userLocation]);

  useEffect(() => {
    const handleResize = () => {
      mapRef.current?.resize();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };
  
    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isExpanded]);  

  useEffect(() => {
    const updateElapsed = () => {
      if (!lastRefreshed) return;

      const diffMs = Date.now() - lastRefreshed;
      const totalMinutes = Math.floor(diffMs / (1000 * 60));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      const formatted = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      setElapsed(formatted);
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 30000);
    return () => clearInterval(interval);
  }, [lastRefreshed]);

  useEffect(() => {
    getStreamData();

    // Cleanup function to run when component unmounts
    return () => {
      // Clean up worker
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      
      // Clean up map instance
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      
      // Note: We don't clear cache on unmount as users might navigate back
      // Cache is only cleared on user change or explicit logout
    };
  }, []);

  const getStreamData = async () => {
    const now: number = Date.now();
    setLastRefreshed(now);
    setLoading(true);
    setError(null);
    
    try {
      // Fetch data from the backend API
      // const data = await getMapData();
      // setMapData(data);
      
      // setTimeout(() => {
      //   setRefreshStep(1);
      // }, 3000);
    } catch (err: any) {
      console.error('Error fetching map data:', err);
      setError(err?.message || 'Failed to fetch map data');
      
      // setTimeout(() => {
      //   setRefreshStep(3); // Show "Click to Refresh" on error
      // }, 1000);
    } finally {
      setLoading(false);
    }
    
  }


  // Create inline worker code
  const getWorkerCode = () => `
// travalerMapWorker.js - Inlined version
// Note: plain JS file for Worker (no DOM access)

const flights = new Map(); // idHash -> flightCompact
const airports = new Map(); // idHash -> airportCompact

const MESSAGE_TYPES = Object.freeze({
  UPDATE_FLIGHTS: 'updateFlights',
  UPDATE_AIRPORTS: 'updateAirports',
  SET_FLIGHT_FILTERS: 'setFlightFilters',
  START: 'start',
  STOP: 'stop',
  SET_INTERVAL_MS: 'setIntervalMs',
  REQUEST_SUMMARY: 'requestSummary',
  SYNC_FLIGHTS: 'syncFlights',
  SYNC_AIRPORTS: 'syncAirports',
  AIRPORT_POSITIONS: 'airportPositions',
  FLIGHT_POSITIONS: 'positions',
});

const SOURCE_TYPE = Object.freeze({
  FLIGHT: 'flight',
  AIRPORT: 'airport',
});

// Inverted indices for fast filter
const idx = {
  passengerFirst: new Map(), // name -> Set(idHash)
  group: new Map(),          // group -> Set(idHash)
  assignee: new Map(),       // assigneename -> Set(idHash)
  recordLocator: new Map(),  // recordLocator -> Set(idHash)
  currentIssue: new Set(),   // Set(idHash)
  futureIssue: new Set(),    // Set(idHash)
  // airports:
  airportCode: new Map(),
  airportPassengerFirst: new Map(), // passenger -> Set(idHash)
  airportGroup: new Map(),          // group -> Set(idHash)
  airportAssignee: new Map(),       // assignee name -> Set(idHash)
  airportRecordLocator: new Map(),  // recordLocator -> Set(idHash)
  airportCurrentIssue: new Set(),   // Set(idHash)
  airportFutureIssue: new Set(),    // Set(idHash)
};

// Global map to store original casing for display purposes
const originalCasingMap = new Map(); // key: lowercase value, value: original casing

let currentFilters = {
  passengerFirst: [], // array of lowercased strings (match any)
  groupNames: [],     // array of lowercased strings
  assigneeNames: [],    // array of strings
  recordLocators: [], // array of lowercased strings
  isCurrentFlightIssue: null, // true/false/null
  isFutureFlightIssue: null   // true/false/null
};

let currentAirportFilters = {
  passengerFirst: [],   // array of lowercased strings
  groupNames: [],       // array of lowercased strings
  assigneeNames: [],    // array of strings
  recordLocators: [], // array of lowercased strings
  airportCodes: [],     // array of lowercased airport codes (match any)
  isCurrentFlightIssue: null,
  isFutureFlightIssue: null
};

let running = false;
let interpIntervalMs = 5000; // 5 seconds
let interpTimer = null;

// Utility: 32-bit hash from string (deterministic)
function hash32(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

// Compact flight storage: only keep needed fields
function compactFlight(raw) {
  const keyStr = raw.id ? String(raw.id) : \`\${raw.flightNumber || ''}|\${raw.utcDepartureTime || ''}\`;
  const idHash = hash32(keyStr);

  const sLat = Number(raw.departureAirport?.geoLocation?.latitude);
  const sLon = Number(raw.departureAirport?.geoLocation?.longitude);
  const eLat = Number(raw.arrivalAirport?.geoLocation?.latitude);
  const eLon = Number(raw.arrivalAirport?.geoLocation?.longitude);

  const depMs = raw.utcDepartureTime ? Date.parse(raw.utcDepartureTime) : NaN;
  const arrMs = raw.utcArrivalTime ? Date.parse(raw.utcArrivalTime) : NaN;

  const depAirportCode = raw.departureAirport?.airportCode;
  const arrAirportCode = raw.arrivalAirport?.airportCode;

  const trips = (raw.trips || []).map(t => {
    const passengers = (t.passengers || []).map(p => {
      const originalName = p.firstName && p.lastName ? 
        (p.firstName + ' ' + p.lastName) : 
        String(p.firstName || p.lastName || '');
      const lowercaseName = originalName.toLowerCase();
      if (originalName && lowercaseName) {
        originalCasingMap.set(lowercaseName, originalName);
      }
      return lowercaseName;
    }).filter(name => name); // Remove empty names
    
    const originalGroupName = t.groupName ? String(t.groupName) : null;
    const groupName = originalGroupName ? originalGroupName.toLowerCase() : null;
    if (originalGroupName && groupName) {
      originalCasingMap.set(groupName, originalGroupName);
    }
    
    const originalRecordLocator = t.recordLocator ? String(t.recordLocator) : null;
    const recordLocator = originalRecordLocator ? originalRecordLocator.toLowerCase() : null;
    if (originalRecordLocator && recordLocator) {
      originalCasingMap.set(recordLocator, originalRecordLocator);
    }
    const tripId = t.tripId;
    const pfhId = t.processedFlightHistoryId;
    const currentFlightHasIssue = t.isCurrentFlightIssue;
    const futureFlightHasIssue = t.isFutureFlightIssue;
    const assigneeName = t.assigneeUserNamesById ? Object.values(t.assigneeUserNamesById || {}).map(name => {
      const originalName = String(name);
      const lowercaseName = originalName.toLowerCase();
      if (originalName && lowercaseName) {
        originalCasingMap.set(lowercaseName, originalName);
      }
      return originalName; // Keep original for assignees (they don't seem to be lowercased in processing)
    }) : [];
    
    return { 
      passengers, 
      groupName, 
      recordLocator,
      assigneeName, 
      tripId, 
      pfhId, 
      currentFlightHasIssue, 
      futureFlightHasIssue 
    };
  });

  const compact = {
    idHash,
    keyStr,
    flightNumber: raw.flightNumber || '',
    start: [sLon || 0, sLat || 0],
    end: [eLon || 0, eLat || 0],
    depMs: isFinite(depMs) ? depMs : null,
    arrMs: isFinite(arrMs) ? arrMs : null,
    depAc: depAirportCode,
    arrAc: arrAirportCode,
    isCurrentFlightIssue: !!raw.isCurrentFlightIssue,
    isFutureFlightIssue: !!raw.isFutureFlightIssue,
    trips
  };

  return compact;
}

// Index helper functions
function addToMapSet(map, key, id) {
  if (!key && key !== 0) return;
  const k = String(key);
  let s = map.get(k);
  if (!s) {
    s = new Set();
    map.set(k, s);
  }
  s.add(id);
}

function removeFromMapSet(map, key, id) {
  if (!key && key !== 0) return;
  const k = String(key);
  const s = map.get(k);
  if (s) {
    s.delete(id);
    if (s.size === 0) map.delete(k);
  }
}

function indexFlight(compact) {
  const id = compact.idHash;
  compact.trips.forEach(t => {
    (t.passengers || []).forEach(name => {
      if (name) addToMapSet(idx.passengerFirst, name, id);
    });
    if (t.groupName) addToMapSet(idx.group, t.groupName, id);
    if (t.recordLocator) addToMapSet(idx.recordLocator, t.recordLocator, id);
    (t.assigneeName || []).forEach(aid => {
      addToMapSet(idx.assignee, aid, id);
    });
  });
  if (compact.isCurrentFlightIssue) idx.currentIssue.add(id);
  if (compact.isFutureFlightIssue) idx.futureIssue.add(id);
}

function deindexFlight(compact) {
  const id = compact.idHash;
  compact.trips.forEach(t => {
    (t.passengers || []).forEach(name => removeFromMapSet(idx.passengerFirst, name, id));
    if (t.groupName) removeFromMapSet(idx.group, t.groupName, id);
    if (t.recordLocator) removeFromMapSet(idx.recordLocator, t.recordLocator, id);
    (t.assigneeName || []).forEach(aid => removeFromMapSet(idx.assignee, aid, id));
  });
  idx.currentIssue.delete(id);
  idx.futureIssue.delete(id);
}

function mergeFlightsArray(arr) {
  const newIds = new Set();
  for (let i = 0; i < arr.length; i++) {
    const raw = arr[i];
    const c = compactFlight(raw);
    newIds.add(c.idHash);

    if (flights.has(c.idHash)) {
      const old = flights.get(c.idHash);
      deindexFlight(old);
      flights.set(c.idHash, c);
      indexFlight(c);
    } else {
      flights.set(c.idHash, c);
      indexFlight(c);
    }
  }

  for (const existingId of Array.from(flights.keys())) {
    if (!newIds.has(existingId)) {
      const removed = flights.get(existingId);
      deindexFlight(removed);
      flights.delete(existingId);
    }
  }
}

function compactAirport(raw) {
  const code = raw.departureAirport?.airportCode || raw.airportCode || raw.code || '';
  const keyStr = String(code).toLowerCase();
  const idHash = hash32(keyStr);

  const lat = Number(raw.departureAirport?.geoLocation?.latitude || raw.geoLocation?.latitude) || 0;
  const lon = Number(raw.departureAirport?.geoLocation?.longitude || raw.geoLocation?.longitude) || 0;

  const trips = (raw.trips || []).map(t => {
    // Process passengers: combine firstName and lastName into full name
    const passengers = (t.passengers || []).map(p => {
      const originalName = p.firstName && p.lastName ? 
        (p.firstName + ' ' + p.lastName) : '';
      const lowercaseName = originalName.toLowerCase();
      if (originalName && lowercaseName) {
        originalCasingMap.set(lowercaseName, originalName);
      }
      return lowercaseName;
    }).filter(name => name); // Remove empty names
    
    const originalGroupName = t.groupName ? String(t.groupName) : null;
    const groupName = originalGroupName ? originalGroupName.toLowerCase() : null;
    if (originalGroupName && groupName) {
      originalCasingMap.set(groupName, originalGroupName);
    }
    
    const originalRecordLocator = t.recordLocator ? String(t.recordLocator) : null;
    const recordLocator = originalRecordLocator ? originalRecordLocator.toLowerCase() : null;
    if (originalRecordLocator && recordLocator) {
      originalCasingMap.set(recordLocator, originalRecordLocator);
    }
    
    // For agent names, we need the VALUES (usernames) from assigneeUserNamesById, not the keys
    const assigneeKeys = t.assigneeUserNamesById ? Object.values(t.assigneeUserNamesById || {}).map(name => {
      const originalName = String(name);
      const lowercaseName = originalName.toLowerCase();
      if (originalName && lowercaseName) {
        originalCasingMap.set(lowercaseName, originalName);
      }
      return originalName; // Keep original for assignees
    }) : [];
    
    const pfhId = t.processedFlightHistoryId;
    const tripId = t.tripId;
    const currentFlightHasIssue = !!t.isCurrentFlightIssue;
    const futureFlightHasIssue = !!t.isFutureFlightIssue;
    return { passengers, groupName, recordLocator, assigneeKeys, pfhId, tripId, currentFlightHasIssue, futureFlightHasIssue };
  });

  return {
    idHash,
    code: keyStr,
    pos: [lon, lat],
    isCurrentFlightIssue: !!raw.isCurrentFlightIssue,
    isFutureFlightIssue: !!raw.isFutureFlightIssue,
    trips
  };
}

function indexAirport(compact) {
  const id = compact.idHash;
  addToMapSet(idx.airportCode, compact.code, id);
  (compact.trips || []).forEach(t => {
    (t.passengers || []).forEach(name => {
      if (name) addToMapSet(idx.airportPassengerFirst, name, id);
    });
    if (t.groupName) addToMapSet(idx.airportGroup, t.groupName, id);
    if (t.recordLocator) addToMapSet(idx.airportRecordLocator, t.recordLocator, id);
    (t.assigneeKeys || []).forEach(aid => addToMapSet(idx.airportAssignee, aid, id));
    if (t.currentFlightHasIssue) idx.airportCurrentIssue.add(id);
    if (t.futureFlightHasIssue) idx.airportFutureIssue.add(id);
  });
}

function deindexAirport(compact) {
  const id = compact.idHash;
  removeFromMapSet(idx.airportCode, compact.code, id);
  (compact.trips || []).forEach(t => {
    (t.passengers || []).forEach(name => removeFromMapSet(idx.airportPassengerFirst, name, id));
    if (t.groupName) removeFromMapSet(idx.airportGroup, t.groupName, id);
    if (t.recordLocator) removeFromMapSet(idx.airportRecordLocator, t.recordLocator, id);
    (t.assigneeKeys || []).forEach(aid => removeFromMapSet(idx.airportAssignee, aid, id));
    idx.airportCurrentIssue.delete(id);
    idx.airportFutureIssue.delete(id);
  });
}

function mergeAirportsArray(arr) {
  const newIds = new Set();
  for (let i = 0; i < arr.length; i++) {
    const raw = arr[i];
    const a = compactAirport(raw);
    newIds.add(a.idHash);

    if (airports.has(a.idHash)) {
      const old = airports.get(a.idHash);
      deindexAirport(old);
      airports.set(a.idHash, a);
      indexAirport(a);
    } else {
      airports.set(a.idHash, a);
      indexAirport(a);
    }
  }

  for (const existingId of Array.from(airports.keys())) {
    if (!newIds.has(existingId)) {
      const rem = airports.get(existingId);
      deindexAirport(rem);
      airports.delete(existingId);
    }
  }
}

function applyFlightFilters() {
  
  let candidate = null;

  const unionFromIndex = (map, keys) => {
    const out = new Set();
    for (let i = 0; i < keys.length; i++) {
      const k = String(keys[i]);
      const s = map.get(k);
      if (s) {
        for (const id of s) out.add(id);
      }
    }
    return out;
  };

  if (currentFilters.passengerFirst && currentFilters.passengerFirst.length) {
    const s = unionFromIndex(idx.passengerFirst, currentFilters.passengerFirst);
    candidate = s;
  }

  if (currentFilters.groupNames && currentFilters.groupNames.length) {
    const s = unionFromIndex(idx.group, currentFilters.groupNames);
    if (candidate === null) candidate = s;
    else candidate = intersection(candidate, s);
  }

  if (currentFilters.recordLocators && currentFilters.recordLocators.length) {
    const s = unionFromIndex(idx.recordLocator, currentFilters.recordLocators);
    if (candidate === null) candidate = s;
    else candidate = intersection(candidate, s);
  }
 
  if (currentFilters.assigneeNames && currentFilters.assigneeNames.length) {
    const s = unionFromIndex(idx.assignee, currentFilters.assigneeNames);
    if (candidate === null) candidate = s;
    else candidate = intersection(candidate, s);
  }

  // Handle issue filters - combine current and future issues with union logic
  let issueFilterSet = null;
  
  if (currentFilters.isCurrentFlightIssue === true || currentFilters.isFutureFlightIssue === true) {
    issueFilterSet = new Set();
    
    if (currentFilters.isCurrentFlightIssue === true) {
      const currentIssueSet = new Set(idx.currentIssue);
      for (const id of currentIssueSet) issueFilterSet.add(id);
    }
    
    if (currentFilters.isFutureFlightIssue === true) {
      const futureIssueSet = new Set(idx.futureIssue);
      for (const id of futureIssueSet) issueFilterSet.add(id);
    }
    
    if (candidate === null) candidate = issueFilterSet;
    else candidate = intersection(candidate, issueFilterSet);
  } 
  // Check if we're filtering AGAINST issues (false values) - only if explicitly set to false
  else if (currentFilters.isCurrentFlightIssue === false || currentFilters.isFutureFlightIssue === false) {
    // Exclude flights with issues
    issueFilterSet = new Set(flights.keys());
    
    if (currentFilters.isCurrentFlightIssue === false) {
      for (const id of idx.currentIssue) issueFilterSet.delete(id);
    }
    
    if (currentFilters.isFutureFlightIssue === false) {
      for (const id of idx.futureIssue) issueFilterSet.delete(id);
    }
    
    if (candidate === null) candidate = issueFilterSet;
    else candidate = intersection(candidate, issueFilterSet);
  }
  // If both are undefined/null, no issue filtering is applied - this handles "All" trips
  else {
    // No issue filtering applied
  }

  if (candidate === null) {
    return Array.from(flights.keys());
  }
  
  const result = Array.from(candidate);
  
  return result;
}

function applyAirportFilters() {
  // Apply normal filtering logic for airports
  // Note: Previously airports were hidden when agent filters were applied,
  // but now we show airports that match the same criteria as flights (including issue-based filtering)
  let candidate = null;

  const unionFromIndexLocal = (map, keys) => {
    const out = new Set();
    for (let i = 0; i < keys.length; i++) {
      const k = String(keys[i]);
      const s = map.get(k);
      if (s) for (const id of s) out.add(id);
    }
    return out;
  };

  if (currentAirportFilters.airportCodes && currentAirportFilters.airportCodes.length) {
    const s = unionFromIndexLocal(idx.airportCode, currentAirportFilters.airportCodes);
    candidate = s;
  }

  if (currentAirportFilters.passengerFirst && currentAirportFilters.passengerFirst.length) {
    const s = unionFromIndexLocal(idx.airportPassengerFirst, currentAirportFilters.passengerFirst);
    candidate = candidate === null ? s : intersection(candidate, s);
  }

  if (currentAirportFilters.groupNames && currentAirportFilters.groupNames.length) {
    const s = unionFromIndexLocal(idx.airportGroup, currentAirportFilters.groupNames);
    candidate = candidate === null ? s : intersection(candidate, s);
  }

  if (currentAirportFilters.recordLocators && currentAirportFilters.recordLocators.length) {
    const s = unionFromIndexLocal(idx.airportRecordLocator, currentAirportFilters.recordLocators);
    candidate = candidate === null ? s : intersection(candidate, s);
  }

  if (currentAirportFilters.assigneeIds && currentAirportFilters.assigneeIds.length) {
    const s = unionFromIndexLocal(idx.airportAssignee, currentAirportFilters.assigneeIds);
    candidate = candidate === null ? s : intersection(candidate, s);
  }

  // Handle airport issue filters - combine current and future issues with union logic (same as flight filtering)
  let airportIssueFilterSet = null;
  
  if (currentAirportFilters.isCurrentFlightIssue === true || currentAirportFilters.isFutureFlightIssue === true) {
    airportIssueFilterSet = new Set();
    
    if (currentAirportFilters.isCurrentFlightIssue === true) {
      const currentIssueSet = new Set(idx.airportCurrentIssue);
      for (const id of currentIssueSet) airportIssueFilterSet.add(id);
    }
    
    if (currentAirportFilters.isFutureFlightIssue === true) {
      const futureIssueSet = new Set(idx.airportFutureIssue);
      for (const id of futureIssueSet) airportIssueFilterSet.add(id);
    }
    
    candidate = candidate === null ? airportIssueFilterSet : intersection(candidate, airportIssueFilterSet);
  } 
  // Check if we're filtering AGAINST issues (false values) - only if explicitly set to false
  else if (currentAirportFilters.isCurrentFlightIssue === false || currentAirportFilters.isFutureFlightIssue === false) {
    // Exclude airports with issues
    airportIssueFilterSet = new Set(airports.keys());
    
    if (currentAirportFilters.isCurrentFlightIssue === false) {
      for (const id of idx.airportCurrentIssue) airportIssueFilterSet.delete(id);
    }
    
    if (currentAirportFilters.isFutureFlightIssue === false) {
      for (const id of idx.airportFutureIssue) airportIssueFilterSet.delete(id);
    }
    
    candidate = candidate === null ? airportIssueFilterSet : intersection(candidate, airportIssueFilterSet);
  }
  // If both are undefined/null, no issue filtering is applied - this handles "All" trips
  else {
    // No issue filtering applied for airports
  }

  if (candidate === null) return Array.from(airports.keys());
  return Array.from(candidate);
}

function extractFilterSuggestions() {
  const passengerNames = new Map(); // key: lowercase, value: original casing
  const groupNames = new Map(); // key: lowercase, value: original casing
  const assigneeNames = new Map(); // key: lowercase, value: original casing
  const recordLocators = new Map(); // key: lowercase, value: original casing
  const agentNames = new Map(); // key: lowercase, value: original casing
  
  // Extract suggestions from flight data
  for (const flight of flights.values()) {
    if (!flight.trips) continue;
    
    for (const trip of flight.trips) {
      if (trip.passengers) {
        trip.passengers.forEach(name => {
          if (name) {
            const originalName = originalCasingMap.get(name) || name;
            passengerNames.set(name, originalName);
          }
        });
      }
      
      if (trip.groupName) {
        const originalName = originalCasingMap.get(trip.groupName) || trip.groupName;
        groupNames.set(trip.groupName, originalName);
      }
      
      if (trip.recordLocator) {
        const originalLocator = originalCasingMap.get(trip.recordLocator) || trip.recordLocator;
        recordLocators.set(trip.recordLocator, originalLocator);
      }
      
      if (trip.assigneeName && Array.isArray(trip.assigneeName)) {
        trip.assigneeName.forEach(name => {
          if (name) {
            const originalName = originalCasingMap.get(name.toLowerCase()) || name;
            assigneeNames.set(name.toLowerCase(), originalName);
            agentNames.set(name.toLowerCase(), originalName);
          }
        });
      }
    }
  }
  
  // Extract suggestions from airport data
  for (const airport of airports.values()) {
    if (!airport.trips) continue;
    
    for (const trip of airport.trips) {
      // Handle passenger names (airport format: array of objects with firstName/lastName)
      if (trip.passengers && Array.isArray(trip.passengers)) {
        trip.passengers.forEach(passengerName => {
          if (passengerName) {
            const originalName = originalCasingMap.get(passengerName) || passengerName;
            passengerNames.set(passengerName, originalName);
          }
        });
      }
      
      if (trip.groupName) {
        const originalName = originalCasingMap.get(trip.groupName) || trip.groupName;
        groupNames.set(trip.groupName, originalName);
      }
      
      if (trip.recordLocator) {
        const originalLocator = originalCasingMap.get(trip.recordLocator) || trip.recordLocator;
        recordLocators.set(trip.recordLocator, originalLocator);
      }
      
      // Handle assignee names from assigneeKeys array
      if (trip.assigneeKeys && Array.isArray(trip.assigneeKeys)) {
        trip.assigneeKeys.forEach(name => {
          if (name) {
            const originalName = originalCasingMap.get(name.toLowerCase()) || name;
            assigneeNames.set(name.toLowerCase(), originalName);
            agentNames.set(name.toLowerCase(), originalName);
          }
        });
      }
    }
  }
  
  return {
    passengerOptions: Array.from(passengerNames.values()).sort().map(name => ({
      tag: name,
      label: name,
      value: name.toLowerCase(), // Keep lowercase for filtering
      originalValue: name // Store original casing for display
    })),
    tripGroupOptions: Array.from(groupNames.values()).sort().map(name => ({
      tag: name,
      label: name,
      value: name.toLowerCase(), // Keep lowercase for filtering
      originalValue: name // Store original casing for display
    })),
    assigneeOptions: Array.from(assigneeNames.values()).sort().map(name => ({
      tag: name,
      label: name,
      value: name.toLowerCase(), // Keep lowercase for filtering
      originalValue: name // Store original casing for display
    })),
    recordLocatorOptions: Array.from(recordLocators.values()).sort().map(locator => ({
      tag: locator,
      label: locator,
      value: locator.toLowerCase(), // Keep lowercase for filtering
      originalValue: locator // Store original casing for display
    })),
    agentNameOptions: Array.from(agentNames.values()).sort().map(name => ({
      tag: name,
      label: name,
      value: name.toLowerCase(), // Keep lowercase for filtering
      originalValue: name // Store original casing for display
    }))
  };
}

function intersection(a, b) {
  if (a.size > b.size) {
    const tmp = a; a = b; b = tmp;
  }
  const out = new Set();
  for (const v of a) {
    if (b.has(v)) out.add(v);
  }
  return out;
}

function interpolateGreatCircle(start, end, t) {
  const [lon1, lat1] = start.map(toRad);
  const [lon2, lat2] = end.map(toRad);

  const d = 2 * Math.asin(
    Math.sqrt(
      Math.sin((lat2 - lat1) / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) *
      Math.sin((lon2 - lon1) / 2) ** 2
    )
  );

  if (d === 0) return start;

  const A = Math.sin((1 - t) * d) / Math.sin(d);
  const B = Math.sin(t * d) / Math.sin(d);

  const x = A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2);
  const y = A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2);
  const z = A * Math.sin(lat1) + B * Math.sin(lat2);

  const lat = Math.atan2(z, Math.sqrt(x * x + y * y));
  const lon = Math.atan2(y, x);
  return [toDeg(lon), toDeg(lat)];
}

function bearing([lon1, lat1], [lon2, lat2]) {
  const φ1 = toRad(lat1), φ2 = toRad(lat2);
  const λ1 = toRad(lon1), λ2 = toRad(lon2);
  const y = Math.sin(λ2 - λ1) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(λ2 - λ1);
  const θ = Math.atan2(y, x);
  return (toDeg(θ) + 360) % 360;
}

function toRad(d) { return d * Math.PI / 180; }
function toDeg(r) { return r * 180 / Math.PI; }

function interpolateLinear(start, end, t) {
  return [ start[0] + (end[0] - start[0]) * t, start[1] + (end[1] - start[1]) * t ];
}

function computeAndPostPositions(idList) {
  const n = idList.length;
  const coords = new Float32Array(n * 3);
  const ids = new Uint32Array(n);

  const now = Date.now();

  for (let i = 0; i < n; i++) {
    const id = idList[i];
    const f = flights.get(id);

    if (!f) {
      coords[i*3 + 0] = 0;
      coords[i*3 + 1] = 0;
      coords[i*3 + 2] = 0;
      ids[i] = 0;
      continue;
    }

    let lon = f.start[0], lat = f.start[1];
    if (f.depMs != null && f.arrMs != null && f.arrMs > f.depMs) {
      const t = Math.min(Math.max((now - f.depMs) / (f.arrMs - f.depMs), 0), 1);
      const pos = interpolateGreatCircle(f.start, f.end, t);
      lon = pos[0];
      lat = pos[1];
    } else {
      lon = f.start[0];
      lat = f.start[1];
    }
    const rot = (bearing(f.start, f.end) - 90 + 360) % 360;

    coords[i*3 + 0] = lon;
    coords[i*3 + 1] = lat;
    coords[i*3 + 2] = rot;

    ids[i] = id;
  }

  postMessage({ type: MESSAGE_TYPES.FLIGHT_POSITIONS, coordsBuffer: coords.buffer, idsBuffer: ids.buffer, length: n }, [coords.buffer, ids.buffer]);
}

function computeAndPostAirportPositions(idList) {
  const n = idList.length;
  const coords = new Float32Array(n * 3);
  const ids = new Uint32Array(n);

  for (let i = 0; i < n; i++) {
    const id = idList[i];
    const airport = airports.get(id);

    if (!airport || !Array.isArray(airport.pos)) {
      coords[i * 3 + 0] = 0;
      coords[i * 3 + 1] = 0;
      coords[i * 3 + 2] = 0;
      ids[i] = 0;
      continue;
    }

    const [lon, lat] = airport.pos;

    coords[i * 3 + 0] = lon;
    coords[i * 3 + 1] = lat;
    coords[i * 3 + 2] = 0; // rotation = 0 for airports
    ids[i] = id;
  }

  postMessage(
    {
      type: MESSAGE_TYPES.AIRPORT_POSITIONS,
      coordsBuffer: coords.buffer,
      idsBuffer: ids.buffer,
      length: n,
    },
    [coords.buffer, ids.buffer]
  );
}

// Public message handler
onmessage = (ev) => {
  const msg = ev.data || {};
  switch (msg.type) {
    case 'updateFlights':
      if (Array.isArray(msg.data)) {
        mergeFlightsArray(msg.data);
        const filterSuggestions = extractFilterSuggestions();
        
        postMessage({
          type: 'syncFlights',
          flights: Array.from(flights.values()),
          filterSuggestions: filterSuggestions,
        });
      }
      break;

    case 'updateAirports':
      if (Array.isArray(msg.data)) {
        mergeAirportsArray(msg.data);
        const filterSuggestions = extractFilterSuggestions();
        
        postMessage({ 
          type: 'syncAirports', 
          airports: Array.from(airports.values()),
          filterSuggestions: filterSuggestions,
        }); 

        const list = applyAirportFilters();
        computeAndPostAirportPositions(list);
      }
      break;

    case 'setFlightFilters':
      
      // Handle issue flags (CRITICAL: This was missing!)
      currentFilters.isCurrentFlightIssue = msg.filters?.isCurrentFlightIssue;
      currentFilters.isFutureFlightIssue = msg.filters?.isFutureFlightIssue;
      
      // Initialize with empty arrays to avoid accumulation issues
      currentFilters.assigneeNames = [];
      currentFilters.recordLocators = [];
 
      if (msg.filters?.passengerFirst && Array.isArray(msg.filters.passengerFirst)) {
        if (msg.filters.passengerFirst.length > 0 && typeof msg.filters.passengerFirst[0] === 'object') {
          currentFilters.passengerFirst = msg.filters.passengerFirst.map(item => String(item.value).toLowerCase());
        } else {
          currentFilters.passengerFirst = msg.filters.passengerFirst.map(s => String(s).toLowerCase());
        }
      } else {
        currentFilters.passengerFirst = [];
      }
 
      if (msg.filters?.groupNames && Array.isArray(msg.filters.groupNames)) {
        if (msg.filters.groupNames.length > 0 && typeof msg.filters.groupNames[0] === 'object') {
          currentFilters.groupNames = msg.filters.groupNames.map(item => String(item.value).toLowerCase());
        } else {
          currentFilters.groupNames = msg.filters.groupNames.map(s => String(s).toLowerCase());
        }
      } else {
        currentFilters.groupNames = [];
      }
 
      // Handle reference number (record locator) filters
      if (msg.filters?.referenceNumber && Array.isArray(msg.filters.referenceNumber)) {
        const referenceValues = msg.filters.referenceNumber.map(item => {
          if (typeof item === 'object' && item !== null) {
            return String(item.value || item.originalValue || item.tag || item).toLowerCase();
          }
          return String(item).toLowerCase();
        });
        currentFilters.recordLocators = [...currentFilters.recordLocators, ...referenceValues];
      }
 
      if (msg.filters?.agentName && Array.isArray(msg.filters.agentName)) {
        // Handle both object format from UI and string format from "My Issues" logic
        const agentValues = msg.filters.agentName.map(item => {
          if (typeof item === 'object' && item !== null) {
            return String(item.originalValue || item.value || item.tag || item);
          }
          return String(item);
        });
        currentFilters.assigneeNames = agentValues;
      } else {
        currentFilters.assigneeNames = [];
      }
      
      const list2 = applyFlightFilters();
      computeAndPostPositions(list2);
      postMessage({ type: 'filtersApplied' });
      break;
    
    case 'setAirportFilters':
      
      // Handle airport issue flags properly (same as flight filters)
      currentAirportFilters.isCurrentFlightIssue = msg.filters?.isCurrentFlightIssue;
      currentAirportFilters.isFutureFlightIssue = msg.filters?.isFutureFlightIssue;
      
      // Initialize with empty arrays to avoid accumulation issues (same as flight filters)
      currentAirportFilters.passengerFirst = [];
      currentAirportFilters.groupNames = [];
      currentAirportFilters.assigneeIds = [];
      currentAirportFilters.recordLocators = [];
      currentAirportFilters.airportCodes = [];
      
      // Handle passenger names with same robust logic as flights
      if (msg.filters?.passengerFirst && Array.isArray(msg.filters.passengerFirst)) {
        if (msg.filters.passengerFirst.length > 0 && typeof msg.filters.passengerFirst[0] === 'object') {
          currentAirportFilters.passengerFirst = msg.filters.passengerFirst.map(item => String(item.value || item.originalValue || item.tag || item).toLowerCase());
        } else {
          currentAirportFilters.passengerFirst = msg.filters.passengerFirst.map(s => String(s).toLowerCase());
        }
      }
      
      // Handle group names with same robust logic as flights
      if (msg.filters?.groupNames && Array.isArray(msg.filters.groupNames)) {
        if (msg.filters.groupNames.length > 0 && typeof msg.filters.groupNames[0] === 'object') {
          currentAirportFilters.groupNames = msg.filters.groupNames.map(item => String(item.value || item.originalValue || item.tag || item).toLowerCase());
        } else {
          currentAirportFilters.groupNames = msg.filters.groupNames.map(s => String(s).toLowerCase());
        }
      }
      
      // Handle record locators with same robust logic as flights
      if (msg.filters?.recordLocators && Array.isArray(msg.filters.recordLocators)) {
        if (msg.filters.recordLocators.length > 0 && typeof msg.filters.recordLocators[0] === 'object') {
          currentAirportFilters.recordLocators = msg.filters.recordLocators.map(item => String(item.value || item.originalValue || item.tag || item).toLowerCase());
        } else {
          currentAirportFilters.recordLocators = msg.filters.recordLocators.map(s => String(s).toLowerCase());
        }
      }

      // Handle assignee IDs with same robust logic as flights
      if (msg.filters?.assigneeIds && Array.isArray(msg.filters.assigneeIds)) {
        const assigneeValues = msg.filters.assigneeIds.map(item => {
          if (typeof item === 'object' && item !== null) {
            return String(item.originalValue || item.value || item.tag || item);
          }
          return String(item);
        });
        currentAirportFilters.assigneeIds = assigneeValues;
      }
      
      // Handle airport codes
      if (msg.filters?.airportCodes && Array.isArray(msg.filters.airportCodes)) {
        currentAirportFilters.airportCodes = msg.filters.airportCodes.map(s => String(s).toLowerCase());
      }
      
      // Apply airport filters and update positions immediately
      const airportList = applyAirportFilters();
      computeAndPostAirportPositions(airportList);
      
      postMessage({ type: 'airportFiltersApplied' });
      break;

    case 'start':
      if (!running) {
        running = true;
        const list = applyFlightFilters();
        computeAndPostPositions(list);
        interpTimer = setInterval(() => {
          const list2 = applyFlightFilters();
          computeAndPostPositions(list2);
        }, interpIntervalMs);
        postMessage({ type: 'started' });
      }
      break;

    case 'stop':
      if (running) {
        running = false;
        if (interpTimer) {
          clearInterval(interpTimer);
          interpTimer = null;
        }
        postMessage({ type: 'stopped' });
      }
      break;

    case 'setIntervalMs':
      if (typeof msg.ms === 'number' && msg.ms > 0) {
        interpIntervalMs = msg.ms;
        if (running) {
          clearInterval(interpTimer);
          interpTimer = setInterval(() => {
            const list3 = applyFlightFilters();
            computeAndPostPositions(list3);
          }, interpIntervalMs);
        }
        postMessage({ type: 'intervalUpdated', ms: interpIntervalMs });
      }
      break;

    case 'requestSummary':
      postMessage({ type: 'updateSummary', flightsCount: flights.size, airportsCount: airports.size });
      break;
    
    case 'requestFilteredAirports':
      {
        const ids = applyAirportFilters();
        const out = ids.map(id => {
          const a = airports.get(id);
          return {
            idHash: a.idHash,
            code: a.code,
            pos: a.pos,
            trips: a.trips
          };
        });
        postMessage({ type: 'filteredAirports', airports: out });
      }
      break;

    case 'requestAirportSummary':
      postMessage({
        type: 'updateAirportSummary',
        airportsCount: airports.size
      });
      break;

    default:
      break;
  }
};
`;

  // //Initialize worker
  useEffect(() => {
    if (!mapIns) return;
    
    // Create worker using inline blob approach (most reliable)
    const createWorker = async () => {
      try {        
        // Create worker from blob (CSP and MIME-safe)
        const workerCode = getWorkerCode();
        const blob = new Blob([workerCode], { type: 'application/javascript' });
        const workerUrl = URL.createObjectURL(blob);
        
        workerRef.current = new Worker(workerUrl);
        
        // Set up worker message handler
        setupWorkerHandlers();
        
        // Start the worker
        workerRef.current.postMessage({ type: "start" });
        
        // Clean up blob URL when done
        URL.revokeObjectURL(workerUrl);
        
      } catch (error) {
        console.error('Failed to create worker from blob:', error);
        
        // Fallback: try direct path approach
        try {
          workerRef.current = new Worker('/worker/travalerMapWorker.js');
          
          setupWorkerHandlers();
          workerRef.current.postMessage({ type: "start" });
        } catch (fallbackError) {
          console.error('All worker creation methods failed:', fallbackError);
          return;
        }
      }
    };

    const setupWorkerHandlers = () => {
      if (!workerRef.current) return;
    workerRef.current.onmessage = (e) => {
      // const { type, positions } = e.data;
      const { type, coordsBuffer, idsBuffer, length, flights, airports, filterSuggestions } = e.data;
      
      // Update filter suggestions whenever received from worker
      if (filterSuggestions) {
        setFilterSuggestions(filterSuggestions);
      }
      
      //Update flights position on map
      if (type === "positions" && coordsBuffer && idsBuffer) {

        const dummyAirport = new Array(length);

        // Recreate Float32Array and Uint32Array from buffers
        const coords = new Float32Array(coordsBuffer);
        const ids = new Uint32Array(idsBuffer);

        const features = new Array(length);
        for (let i = 0; i < length; i++) {
          // Construct minimal GeoJSON Feature
          // Avoid extra props or stringification
          const lon = coords[i * 3];
          const lat = coords[i * 3 + 1];
          const id = ids[i];

          const cachedFlight = flightMirrorCache.current.get(id);
          let icon = "flight-green";
          let showWarning = false;
          let tripCount = 0;
          let countIconId = "";

          if (cachedFlight) {
            tripCount = cachedFlight.trips?.length || 0;
            
            if (cachedFlight.isCurrentFlightIssue) {
              icon = "flight-red";
            } else if (cachedFlight.isFutureFlightIssue) {
              icon = "flight-green-warning";
              showWarning = true;
            }

            // Generate count icon if trip count > 1
            if (tripCount > 1) {
              countIconId = `flight-trip-count-${tripCount}`;
              if (!mapRef.current?.hasImage(countIconId)) {
                const svg = CountIcon(tripCount.toString());
                loadSvgIconToMapbox(mapRef.current!, countIconId, svg);
              }
            }
          }

          features[i] = {
            type: "Feature",
            geometry: { type: "Point", coordinates: [lon, lat] },
            properties: { 
              flightId: ids[i], 
              icon, 
              showWarning, 
              rotation: coords[i * 3 + 2],
              tripCount,
              countIcon: countIconId
            },
          };

        }
       
        const geojson: GeoJSON.FeatureCollection<GeoJSON.Point> = {
          type: "FeatureCollection",
          features,
        };


        const src = mapRef.current?.getSource("flights-source") as GeoJSONSource;
        if (src) {
          src.setData(geojson);
          // Mark flights as rendered
          setFlightsRendered(true);
          // src.setData(geojson1);
        }
      }

      //Update airports positons on map
      if (type === "airportPositions" && coordsBuffer && idsBuffer) {

        // Recreate Float32Array and Uint32Array from buffers
        const coords = new Float32Array(coordsBuffer);
        const ids = new Uint32Array(idsBuffer);
        const features = new Array(length);
        for (let i = 0; i < length; i++) {
          // Construct minimal GeoJSON Feature
          // Avoid extra props or stringification
          const lon = coords[i * 3];
          const lat = coords[i * 3 + 1];
          const id = ids[i];

          
          const cachedAirport = airportMirrorCache.current.get(id);

          let tripCount = cachedAirport.trips?.length;
          let iconId = "airport-location";
          if (tripCount > 0) {
            iconId = `trip-count-${tripCount}`;
            if (!mapRef.current?.hasImage(iconId)) {
              const svg = CountIcon(tripCount.toString());
              loadSvgIconToMapbox(mapRef.current!, iconId, svg);
            }
          }

          
          features[i] = {
            type: "Feature",
            geometry: { type: "Point", coordinates: [lon, lat] },
            properties: { id, name, count: tripCount, countIcon: iconId },
          };

        }
       
        const geojson: GeoJSON.FeatureCollection<GeoJSON.Point> = {
          type: "FeatureCollection",
          features,
        };

        const src = mapRef.current?.getSource("airports-source") as GeoJSONSource;
        if (src) {
          src.setData(geojson);
          // Mark airports as rendered
          setAirportsRendered(true);
        }

      }


      // 🔹 Sync compact flights from worker
      if (type === "syncFlights" && Array.isArray(flights)) {
        // Efficient rebuild — clear and bulk-insert
        const cache = flightMirrorCache.current;
        cache.clear();
        for (const f of flights) {
          cache.set(f.idHash, f);
        }
        
        // Update filter suggestions from worker
        if (filterSuggestions) {
          setFilterSuggestions(filterSuggestions);
        }
        return; // Done
      }

      // 🔹 Sync compact airports from worker
      if (type === "syncAirports" && Array.isArray(airports)) {
        // Efficient rebuild — clear and bulk-insert
        const airportCache = airportMirrorCache.current;
        airportCache.clear();
        for (const a of airports) {
          airportCache.set(a.idHash, a);
        }
        
        // Update filter suggestions if received from worker
        if (filterSuggestions) {
          setFilterSuggestions(filterSuggestions);
        }
        
        return; // Done
      }

    };
    };

    // Initialize worker creation
    createWorker();

    // Cleanup on unmount
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [mapIns]);

  // Function to get active flights data
  const getActiveFlights = async () => {
      // Dummy API simulation (replace later)
      try {
        //You can add optional query params here
        const queryParams = {};
        const flights = await fetchActiveFlights({});  

        return flights || [];                
      } catch (error) {
        console.error("Error fetching active flights:", error);
        return [];
      }
  };

  // Function to get active airports data
  const getActiveAirports = async () => {
      try{
        const airports = await fetchActiveAirports({});
        return airports || []; 

        } catch (error) {
        console.error("Error fetching active airports:", error);
        return [];
      }
  };

  // Function to fetch and send data to worker
  const fetchAndSend = async () => {
    if (!mapIns || !workerRef.current) return;

    // Only set loading true if not already loading (to avoid flickering on auto-refresh)
    if (!loading) {
      setLoading(true);
    }
    setError(null);
    
    // Reset all flags for new data fetch
    setDataFetched(false);
    setFlightsRendered(false);
    setAirportsRendered(false);

    try {
      const [flights, airports] = await Promise.all([
        getActiveFlights(),
        getActiveAirports(),
      ]);

      // Send flights data to worker
      workerRef.current?.postMessage({ type: 'updateFlights', data: flights });

      // Send airports data to worker
      workerRef.current?.postMessage({ type: 'updateAirports', data: airports });

      // Update last refreshed time for UI
      const now: number = Date.now();
      setLastRefreshed(now);

      // Reset refresh step to normal state on success
      setRefreshStep(1);

      // Mark data as fetched - loading will be hidden when rendering is complete
      setDataFetched(true);
      
      // Mark initial load as complete
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }

    } catch (err: any) {
      setError(err?.message || 'Failed to fetch flight/airport data');
      setRefreshStep(3); // Set to error/retry state
      setLoading(false); // Hide loading on error
    }
  };

  // Auto-refresh useEffect - every 5 mins
  useEffect(() => {
    if (!mapIns) return;

    // Ensure loading state is shown for initial load
    if (isInitialLoad && !loading) {
      setLoading(true);
    }

    // Initial load
    fetchAndSend();
    
    // Set up auto-refresh timer (5 minutes = 300,000ms)
    const timer = setInterval(fetchAndSend, 300000);
    return () => clearInterval(timer);
  }, [mapIns]);


  const handleZoomIn = () => {
    mapRef.current?.zoomIn();
  };

  const handleZoomOut = () => {
    mapRef.current?.zoomOut();
  };

  let isExpandClass = isExpanded ? 'expandView' : '';
  let refreshTemp = <>
    <div className="text">{'Auto-refresh'}</div>
    <div className="lastRefresh">{`Last refreshed ${elapsed}mins ago`}</div>
  </>;
  if (refreshStep === 2 || loading) {
    refreshTemp = <div className="text">
      <div style={{
        width: "12px",
        height: "12px", 
        border: "2px solid rgba(255, 255, 255, 0.3)",
        borderTop: "2px solid #ffffff",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
        display: "inline-block"
      }} />
    </div>;
  } else if (refreshStep === 3) {
    refreshTemp = <>
      <div className="text">{error ? 'Click to Retry' : 'Click to Refresh'}</div>
      <div className="lastRefresh">{`Last refreshed ${elapsed}mins ago`}</div>
    </>;
  }
  return (
    <>
      {/* CSS Animation for loading spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="mapContainer" style={{ position: "relative" }}>
        <div
          ref={mapContainerRef}
          style={{
            width: isExpanded ? "100vw" : "100%",
            height: isExpanded ? "100vh" : "60vh",
            transition: "all 0.3s ease-in-out",
            position: isExpanded ? "fixed" : "relative",
            top: isExpanded ? 0 : undefined,
            left: isExpanded ? 0 : undefined,
            zIndex: 1,
          }}
        />

        {/* ✅ Flight Popup */}
        {hoveredFlight && mapIns && (
          <FlightPopup
            map={mapIns}
            lngLat={hoveredFlight.lngLat}
            info={hoveredFlight.info}
            popupShouldBeOnLeft={hoveredFlight.popupShouldBeOnLeft}
            onClose={() => setHoveredFlight(null)}
          />
        )}

        {/* Loading overlay */}
        {loading && (
          <div
            className="loading-overlay"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                color: "white",
                padding: "40px",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
              }}
            >
              {/* Loading Spinner */}  
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  border: "6px solid rgba(255, 255, 255, 0.3)",
                  borderTop: "6px solid #ffffff",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
            </div>
          </div>
        )}

        {/* Error overlay */}
        {error && (
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "50%",
              transform: "translateX(-50%)",
              background: "rgba(255, 0, 0, 0.8)",
              color: "white",
              padding: "10px",
              borderRadius: "5px",
              zIndex: 10,
            }}
          >
            Error: {error}
          </div>
        )}

        {/* Filter and map controls */}
        <div
          className={`mapBtn filterIcon ${modeChange} ${isExpandClass}`}
          onClick={() => {
            setShowDetailedFilter(!showDetailedFilter);
          }}
        >
          <span className="countRound">
            <span className="count">{selectionFilterCount}</span>
          </span>
          <FilterIcon />
        </div>

        <div
          className={`refreshBox ${modeChange} ${isExpandClass}`}
        >
          <RefreshIcon />
          <div
            className={`refreshContent ${modeChange}`}
            onClick={() => {
              setRefreshStep(2);
              setError(null);
              fetchAndSend();
            }}
          >
            {refreshTemp}
          </div>
        </div>

        <div
          className={`mapBtn expandIcon ${modeChange} ${isExpandClass} ${
            isExpanded ? "expanded" : ""
          }`}
          onClick={() => {
            setIsExpanded(!isExpanded);
            setShowDetailedFilter(false);
            if (filterRef.current) {
              const filterResults = filterRef.current?.searchResults();
              setSelectionFilter(filterResults);
              setSelectionFilterCount(
                filterRef.current?.getFilterCount() || 1
              );
            }
            setTimeout(() => {
              mapRef.current?.resize();
            }, 300);
          }}
        >
          <ExpandIcon />
        </div>

        <div
          className={`mapBtn plusIcon ${modeChange} ${isExpandClass}`}
          onClick={handleZoomIn}
        >
          <PlusIcon />
        </div>

        <div
          className={`mapBtn minusIcon ${modeChange} ${isExpandClass}`}
          onClick={handleZoomOut}
        >
          <MinusIcon />
        </div>

        {/* Filter panel */}
        {showDetailedFilter && (
          <>
            <FilterComponent
              ref={filterRef}
              close={(searchResults, keepOpen) => {
                if (!keepOpen) {
                  setShowDetailedFilter(!showDetailedFilter);
                }
                
                setSelectionFilter(searchResults);
                
                const count = calculateFilterCount(searchResults);
                setSelectionFilterCount(count);
              }}
              isExpandClass={isExpandClass}
              selectionFilter={selectionFilter}
              passengerOptions={filterSuggestions.passengerOptions}
              tripGroupOptions={filterSuggestions.tripGroupOptions}
              multiSelectOptions={filterSuggestions.assigneeOptions}
              recordLocatorOptions={filterSuggestions.recordLocatorOptions}
              agentNameOptions={filterSuggestions.agentNameOptions}
            />
          </>
        )}
        
      </div>
    </>
  );

};

export default MapComponent;